class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.openSession = 0;
    this.openTimer = undefined;
    this.renderTimer = undefined;
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  focusDrawer(session, force = false) {
    if (session !== this.openSession || !this.classList.contains('active')) return;

    const activeElement = document.activeElement;
    if (
      !force &&
      activeElement !== document.body &&
      activeElement?.isConnected &&
      activeElement !== this.activeElement &&
      !this.contains(activeElement)
    ) {
      return;
    }

    if (!force && this.contains(activeElement)) return;

    const container = this.classList.contains('is-empty')
      ? this.querySelector('.drawer__inner-empty')
      : this.querySelector('#CartDrawer');
    const closeButton = this.querySelector('.drawer__close');
    if (container && closeButton) trapFocus(container, closeButton);
  }

  getInteractionSession() {
    return this.openSession;
  }

  isInteractionSessionActive(session) {
    return this.classList.contains('active') && this.openSession === session;
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    const session = ++this.openSession;
    clearTimeout(this.openTimer);
    clearTimeout(this.focusTimer);
    cancelAnimationFrame(this.focusFrame);
    if (this.focusReady) this.removeEventListener('transitionend', this.focusReady);
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const previewBar = document.querySelector('#preview-bar-iframe, #PBarNextFrame');
    const previewHeight = previewBar?.getBoundingClientRect().height || 0;
    const previewInset = previewHeight > 100 ? 64 : previewHeight;
    this.style.setProperty('--z-cart-preview-height', `${previewInset}px`);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    const focusReady = (event) => {
      if (session !== this.openSession) return;
      if (event && event.target !== this.querySelector('.drawer__inner')) return;
      this.removeEventListener('transitionend', focusReady);
      clearTimeout(this.focusTimer);
      this.focusDrawer(session);
    };
    this.focusReady = focusReady;
    this.addEventListener('transitionend', focusReady);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    this.openTimer = setTimeout(() => {
      if (session !== this.openSession) return;
      this.classList.add('animate', 'active');
      const duration = getComputedStyle(this.querySelector('.drawer__inner')).transitionDuration;
      this.focusDrawer(session, true);
      this.focusFrame = requestAnimationFrame(() => {
        if (session !== this.openSession) return;
        this.focusFrame = undefined;
        this.focusDrawer(session);
      });
      if (matchMedia('(prefers-reduced-motion: reduce)').matches || !duration.split(',').some(value => parseFloat(value) > 0)) {
        this.removeEventListener('transitionend', focusReady);
      } else {
        this.focusTimer = setTimeout(() => focusReady(), 600);
      }
    });

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    ++this.openSession;
    clearTimeout(this.openTimer);
    clearTimeout(this.renderTimer);
    clearTimeout(this.focusTimer);
    cancelAnimationFrame(this.focusFrame);
    if (this.focusReady) this.removeEventListener('transitionend', this.focusReady);
    this.classList.remove('active');
    if (this.contains(document.activeElement) || document.activeElement === document.body) {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const wasActive = this.classList.contains('active');
    const session = this.openSession;
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    clearTimeout(this.renderTimer);
    this.renderTimer = setTimeout(() => {
      if (session !== this.openSession) return;
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      if (wasActive) this.focusDrawer(session);
      else this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


